// GraphQL
import { Country_Enum } from "@/graphql";

class EnumObjectDecorator<EnumObject extends { [key: string]: unknown }> {
  private obj: EnumObject;

  constructor(objValue: EnumObject) {
    this.obj = objValue;
  }

  // ///////////////////
  // Default getters
  // ///////////////////

  // Method to get the object
  get(): EnumObject {
    return this.obj;
  }

  // Method to get the object as an array
  getAsArray(): Array<{ key: string; value: EnumObject[keyof EnumObject] }> {
    return Object.keys(this.obj).map((key: string) => ({
      key,
      value: this.obj[key as keyof EnumObject],
    }));
  }

  // Method to get the object keys
  getKeys(): string[] {
    return Object.keys(this.obj);
  }

  // Method to get the object values
  getValues(): Array<EnumObject[keyof EnumObject]> {
    return Object.values(this.obj) as Array<EnumObject[keyof EnumObject]>;
  }

  // Method to get the key from the value
  getKeyFromValue(value: EnumObject[keyof EnumObject]): string {
    return Object.keys(this.obj).find((key) => this.obj[key] === value) || "";
  }

  // Method to get the value from the key
  getValueFromKey(key: string): EnumObject[keyof EnumObject] {
    return this.obj[key as keyof EnumObject];
  }

  // ///////////////////
  // Localization getters
  // ///////////////////

  // Method to get the localized object
  getLocalized(locale: Country_Enum): EnumObject {
    return this.obj[locale] as EnumObject;
  }

  // Method to get the localized object as an array
  getLocalizedAsArray(locale: Country_Enum): Array<{ key: string; value: unknown }> {
    const localeObj = this.obj[locale] as Record<string, unknown>;
    return Object.keys(localeObj).map((key: string) => ({
      key,
      value: localeObj[key],
    }));
  }

  // Method to get the localized object keys
  getLocalizedKeys(locale: Country_Enum): string[] {
    return Object.keys(this.obj[locale] as Record<string, unknown>);
  }

  // Method to get the localized object values
  getLocalizedValues(locale: Country_Enum): unknown[] {
    return Object.values(this.obj[locale] as Record<string, unknown>);
  }

  // Method to get the localized key from the value
  getLocalizedKeyFromValue(locale: Country_Enum, value: unknown): string {
    const localeObj = this.obj[locale] as Record<string, unknown>;
    return Object.keys(localeObj).find((key) => localeObj[key] === value) || "";
  }

  // Method to get the localized value from the key
  getLocalizedValueFromKey(locale: Country_Enum, key: string): unknown {
    return (this.obj[locale] as Record<string, unknown>)[key];
  }
}

export default EnumObjectDecorator;
